import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BlockStack, Icon, TextField } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { CustomCard } from '@/components';
import { tokenSelector } from '@/redux/features/auth.slice';
import { useGetIntegrationsStatusQuery } from '@/redux/api/api.caller';
import { getIntegrationsConfig } from './config';
import useAvadaIntegrationAdapter from './adapters/useAvadaIntegrationAdapter';
import useTrustpilotIntegrationAdapter from './adapters/useTrustpilotIntegrationAdapter';
import useTrustpilotConfigTab from './configTabs/trustpilot/useTrustpilotConfigTab';
import IntegrationList from './components/IntegrationList';
import IntegrationPanel from './components/IntegrationPanel';
import { StyledIntegrationSearch } from './styled';

const Integration = () => {
  const { shop } = useSelector(tokenSelector);
  const [search, setSearch] = useState('');
  const [activeIntegrationId, setActiveIntegrationId] = useState<string | null>(null);

  const { data: statusData, isFetching: isStatusFetching } = useGetIntegrationsStatusQuery();
  const avadaAdapter = useAvadaIntegrationAdapter(statusData?.data?.avada, isStatusFetching);
  const trustpilotAdapter = useTrustpilotIntegrationAdapter(statusData?.data?.trustpilot, isStatusFetching);
  const adaptersById = useMemo(() => ({ avada: avadaAdapter, trustpilot: trustpilotAdapter }), [avadaAdapter, trustpilotAdapter]);

  const trustpilotConfigTab = useTrustpilotConfigTab(statusData?.data?.trustpilot, trustpilotAdapter.status);
  const configTabsById = useMemo(() => ({ trustpilot: trustpilotConfigTab }), [trustpilotConfigTab]);

  const integrationsConfig = useMemo(() => getIntegrationsConfig(shop), [shop]);

  const filteredConfigs = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return integrationsConfig.filter(
      (config) => config.title.toLowerCase().includes(keyword) || config.id.toLowerCase().includes(keyword),
    );
  }, [integrationsConfig, search]);

  const activeConfig = integrationsConfig.find((config) => config.id === activeIntegrationId);
  const activeAdapter = activeConfig ? adaptersById[activeConfig.id as keyof typeof adaptersById] : undefined;

  return (
    <>
      <CustomCard
        title="Integration"
        actionGroup={[
          {
            content: (
              <StyledIntegrationSearch>
                <TextField
                  label=""
                  labelHidden
                  value={search}
                  onChange={setSearch}
                  autoComplete="off"
                  suffix={<Icon source={SearchIcon} tone="base" />}
                />
              </StyledIntegrationSearch>
            ),
          },
        ]}
      >
        <BlockStack gap="400">
          <IntegrationList items={filteredConfigs} adaptersById={adaptersById} onOpenPanel={setActiveIntegrationId} />
        </BlockStack>
      </CustomCard>

      {activeConfig && activeAdapter && (
        <IntegrationPanel
          config={activeConfig}
          adapter={activeAdapter}
          configTab={configTabsById[activeConfig.id as keyof typeof configTabsById]}
          open={!!activeIntegrationId}
          onClose={() => setActiveIntegrationId(null)}
        />
      )}
    </>
  );
};

export default Integration;
